/**
 * @fileoverview Source URLs bar — shows which CMS pages a section's data came from.
 *
 * Each URL is a clickable chip that snaps the browser agent viewer to the
 * corresponding step (matched by URL).
 */
import { ExternalLink } from 'lucide-react';
import React from 'react';

import { cn } from '@/lib/utils';

interface SourceUrlsBarProps {
  urls: string[];
  onSnapToUrl?: (url: string) => void;
}

/**
 * Extract a short label from a CMS URL for display.
 * e.g. "https://firm.filevineapp.com/#/project/123/activity" → "activity"
 * e.g. "https://firm.filevineapp.com/#/project/123/contacts" → "contacts"
 * Falls back to the last path segment or the hostname.
 */
function labelFromUrl(url: string): string {
  try {
    const u = new URL(url);
    // Filevine uses hash-based routing: /#/project/123/section
    const fragment = u.hash.replace(/^#\/?/, '');
    const parts = fragment.split('/').filter(Boolean);
    // Skip "project" and the ID, take the rest
    const projectIdx = parts.indexOf('project');
    if (projectIdx >= 0 && parts.length > projectIdx + 2) {
      return parts.slice(projectIdx + 2).join('/') || 'project';
    }
    // Fallback: last path segment
    return parts[parts.length - 1] || u.hostname;
  } catch {
    return url.slice(0, 30);
  }
}

export const SourceUrlsBar: React.FC<SourceUrlsBarProps> = ({ urls, onSnapToUrl }) => {
  if (urls.length === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-1.5 py-1">
      <span className="text-[10px] font-medium text-muted-foreground">Sources:</span>
      {urls.map((url) => (
        <button
          type="button"
          key={url}
          onClick={() => onSnapToUrl?.(url)}
          title={url}
          className={cn(
            'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium transition',
            onSnapToUrl
              ? 'border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 dark:border-blue-800 dark:bg-blue-950 dark:text-blue-300 dark:hover:bg-blue-900 cursor-pointer'
              : 'border-zinc-200 bg-zinc-50 text-zinc-600 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-400',
          )}
        >
          <ExternalLink className="h-2.5 w-2.5" />
          {labelFromUrl(url)}
        </button>
      ))}
    </div>
  );
};
